import {
  Button,
  Css,
  InlineFeedbackBanner,
  InlineFeedbackBannerType,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Tokens,
  useModal,
} from "@homebound/beam";
import { useNavigate } from "react-router-dom";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { ProductOfferingsOverviewTab_ProductOfferingFragment } from "src/routes/libraries/product-offerings/overview/ProductOfferingsOverviewTab.generated";
import { createProjectProductOfferingUrl } from "src/RouteUrls";
import { formatList } from "src/utils";

/** `type` is always "error" right now; a non-blocking check would add a "warning". */
type ProductOfferingError = { type: InlineFeedbackBannerType; tagText: string; messages: string[] };

/**
 * The validation errors that block publishing the offering's draft.
 */
export function productOfferingError(
  po: ProductOfferingsOverviewTab_ProductOfferingFragment,
): ProductOfferingError | undefined {
  if (po.effectiveStatus.code === ReadyPlanStatus.Archived || po.copyInProgress) return undefined;
  const reasons = po.aggregateDraft?.canPublish.disabledReasons ?? [];
  if (reasons.isEmpty) return undefined;
  return { type: "error", tagText: "Publishing Errors", messages: reasons.map((r) => r.message) };
}

type ProductOfferingErrorBannerProps = {
  communityId: string;
  productOffering: ProductOfferingsOverviewTab_ProductOfferingFragment;
  error: ProductOfferingError;
};

/** The offering row's companion. */
export function ProductOfferingErrorBanner(props: ProductOfferingErrorBannerProps) {
  const { communityId, productOffering, error } = props;
  const { openModal } = useModal();
  return (
    <InlineFeedbackBanner
      type={error.type}
      tagText={error.tagText}
      // One line so a row with many reasons stays short; the modal has the full list
      description={<div css={Css.truncate.$}>{formatList(error.messages)}</div>}
      actions={[
        {
          label: "Review errors",
          onClick: () =>
            openModal({
              content: (
                <ProductOfferingErrorsModal communityId={communityId} productOffering={productOffering} error={error} />
              ),
            }),
        },
      ]}
    />
  );
}

/** The banner's errors, one per line, with a link to the offering page where they're fixed. */
function ProductOfferingErrorsModal(props: ProductOfferingErrorBannerProps) {
  const { communityId, productOffering, error } = props;
  const { closeModal } = useModal();
  const navigate = useNavigate();
  return (
    <>
      <ModalHeader>{error.tagText}</ModalHeader>
      <ModalBody>
        <div css={Css.sm.color(Tokens.OnSurfaceMuted).mb2.$}>
          {productOffering.name} can't be published until these are resolved:
        </div>
        <ul css={Css.sm.m0.pl3.df.fdc.gap1.$}>
          {error.messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="tertiary"
          label="View product offering"
          onClick={() => {
            closeModal();
            navigate(
              createProjectProductOfferingUrl(communityId, productOffering.id, productOffering.aggregateLatest.id),
            );
          }}
        />
        <Button label="Close" onClick={closeModal} />
      </ModalFooter>
    </>
  );
}
